// src/core/store/auth.ts
// src/core/store/auth.ts

import { defineStore } from 'pinia'
import { removeAuthHeader, setAuthHeader } from '@/core/config/axios'
import { ref, computed } from 'vue'
import {
  useLoadAuth,
  useLogin,
  useLogout,
  useRequestPasswordChange,
  useResetPassword,
} from '../api'
import Cookies from 'cookies-ts'
import { useLocalStorage, useToggle } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { TUser, TToken } from '@/core/types'

type TSetTokenConfig = {
  fetchUser?: boolean
  refreshToken?: boolean
  redirectRouteName?: string
}

type TSetTokenOptions = {
  tokenSettings: TToken
  config?: TSetTokenConfig
}

export const useAuthStore = defineStore('auth', () => {
  const cookies = new Cookies()
  const router = useRouter()

  const token = useLocalStorage<string | null | undefined>(
    'access_token',
    undefined,
  )

  const user = ref<TUser | undefined>(undefined)
  const [isAuthenticating, toggleIsAuthenticating] = useToggle(true)

  const { refetch: onLoadUser } = useLoadAuth({
    enabled: false,
  })

  const { mutateAsync: onRequestRecover, isPending: isRecoveryPassword } =
    useRequestPasswordChange({
      handlers: {
        onSuccess: async () => {
          router.replace({ name: 'passwordRecoveryConfirmation' })
        },
      },
    })

  const {
    mutateAsync: onResetPassword,
    isPending: isResetPasswordLocal,
    error: resetError,
  } = useResetPassword({
    handlers: {
      // handlers
    },
  })

  const {
    mutateAsync: onLogin,
    isPending: isLogging,
    error: loginError,
  } = useLogin({
    handlers: {
      onSuccess: async (response) => {
        if (response) {
          await setToken({
            tokenSettings: response,
            config: {
              fetchUser: true,
              redirectRouteName: 'dashboard',
            },
          })
        }
      },
    },
  })

  const { mutateAsync: onLogout } = useLogout({
    handlers: {
      onSuccess: () => {
        toggleIsAuthenticating(true)
        onLogoutFn()
      },
      onError: () => {
        // Even if logout fails on server, clear local state
        toggleIsAuthenticating(true)
        onLogoutFn()
      },
    },
  })

  const onLogoutFn = () => {
    token.value = undefined
    user.value = undefined
    localStorage.removeItem('access_token')
    cookies.remove('access_token')
    removeAuthHeader()
    router.replace({ name: 'login', params: { logout: 'ok' } })
    toggleIsAuthenticating(false)
  }

  const __isLoadingUser = ref(false)

  const onFetchUser = async () => {
    __isLoadingUser.value = true
    await onLoadUser()
      .then((response) => {
        if (response.data && response.data?.uuid) {
          setUser(response.data)
        } else {
          onLogoutFn()
        }
      })
      .catch(() => {
        onLogoutFn()
      })
      .finally(() => {
        __isLoadingUser.value = false
        toggleIsAuthenticating(false)
      })
  }

  const checkAuth = async () => {
    const savedToken = token.value || cookies.get('access_token')

    if (savedToken) {
      setAuthHeader(savedToken)
      await onFetchUser()
    } else {
      toggleIsAuthenticating(false)
    }
  }

  const setToken = async (options: TSetTokenOptions) => {
    const { tokenSettings, config } = options

    token.value = tokenSettings.access_token

    cookies.set('access_token', tokenSettings.access_token, {
      expires: tokenSettings.expires_in,
      secure: true,
    })

    setAuthHeader(tokenSettings.access_token)

    if (config?.fetchUser) {
      await onFetchUser()
    }

    if (config?.redirectRouteName) {
      await router.push({ name: config.redirectRouteName })
    }
  }

  const setUser = (payload: TUser) => {
    user.value = payload
    toggleIsAuthenticating(false)
  }

  // Computed properties
  const isAuthenticated = computed(() => !!user.value)
  const userRoles = computed(() => user.value?.roles || [])

  // Loaders
  const loaders = computed(() => ({
    isLoadingUser: __isLoadingUser.value,
    isLogging: isLogging.value,
    isAuthenticating: isAuthenticating.value,
    isResetPassword: isResetPasswordLocal.value,
    isRecoveryPassword: isRecoveryPassword.value,
  }))

  //Errors
  const errorsBag = computed(() => ({
    login: loginError?.value?.response?.data,
    reset: resetError?.value?.response?.data,
    recovery: null,
  }))

  return {
    errorsBag,
    loaders,
    token,
    user,
    isAuthenticated,
    userRoles,
    onResetPassword,
    onLogin,
    onLogout,
    setUser,
    checkAuth,
    onLogoutFn,
    onRequestRecover,
    setToken,
  }
})
